/**
 * Os três separadores da página de produto.
 *
 * Um aberto de cada vez. Abrir e fechar anima-se em CSS, com grid-template-rows
 * de 0fr para 1fr — assim não é preciso medir o painel aqui dentro, que é onde
 * este género de animação parte quando o texto muda de tamanho.
 *
 * Trocar de separador é outra coisa: a altura muda de repente porque o conteúdo
 * é outro. Aí esbate-se o painel, troca-se por baixo, e volta-se a mostrar — o
 * salto acontece com ele apagado.
 */
const ESBATER = 160;
const REACENDER = ['.claro-info__brilho', '.claro-info__anel', '.claro-info__dentro'];

class ClaroInfo extends HTMLElement {
  connectedCallback() {
    this.botoes = [...this.querySelectorAll('[role="tab"]')];
    this.paineis = [...this.querySelectorAll('[role="tabpanel"]')];

    this.parado = matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.#guardaAltura();
    addEventListener('resize', () => this.#guardaAltura(), { passive: true });

    this.botoes.forEach((botao, i) => {
      botao.addEventListener('click', () => this.#carrega(i));
      botao.addEventListener('keydown', (evento) => this.#tecla(evento, i));
    });
  }

  /**
   * Mede o painel mais alto e guarda a altura dele.
   *
   * Trocar de separador troca textos de tamanhos diferentes, e a coluna mudava
   * de altura a cada carregada. A galeria está colada e, à altura destes
   * botões, já chegou ao fim do percurso — encostada ao fundo da grelha. Mexer
   * na altura da grelha arrastava-a com ela. Reservando a altura do maior,
   * trocar de separador deixa de mexer em nada.
   */
  #guardaAltura() {
    const escondidos = this.paineis.map((p) => p.hidden);
    this.paineis.forEach((p) => (p.hidden = false));

    const maior = Math.max(...this.paineis.map((p) => p.offsetHeight));

    this.paineis.forEach((p, i) => (p.hidden = escondidos[i]));
    this.style.setProperty('--claro-info-alto', `${maior}px`);
  }

  /** Carregar no separador aberto fecha-o; noutro qualquer, troca */
  #carrega(i) {
    const aberto = this.hasAttribute('data-aberto');
    const jaEste = this.botoes[i].getAttribute('aria-selected') === 'true';

    this.#apaga();

    if (aberto && jaEste) {
      this.removeAttribute('data-aberto');
      this.botoes[i].setAttribute('aria-selected', 'false');
      return;
    }

    this.botoes.forEach((b, n) => b.setAttribute('aria-selected', String(n === i)));
    this.#acende(this.botoes[i]);

    // Fechado: não há nada a esbater, é só abrir
    if (!aberto || this.parado) {
      this.#mostra(i);
      this.setAttribute('data-aberto', '');
      return;
    }

    clearTimeout(this.espera);
    this.setAttribute('data-a-trocar', '');
    this.espera = setTimeout(() => {
      this.#mostra(i);
      this.removeAttribute('data-a-trocar');
    }, ESBATER);
  }

  #mostra(i) {
    this.paineis.forEach((p, n) => (p.hidden = n !== i));
  }

  /**
   * O brilho, o anel e o salto correm uma vez e ficam no fim. Para voltarem a
   * correr não chega tirar e pôr a classe: o navegador junta as duas coisas
   * numa só passagem e a animação não recomeça. Ler uma medida no meio força-o
   * a assentar o que já fez.
   */
  #apaga() {
    this.querySelectorAll('.a-correr').forEach((el) => {
      el.classList.remove('a-correr');
      void el.offsetWidth;
    });
  }

  #acende(botao) {
    if (this.parado) return;
    REACENDER.forEach((qual) => botao.querySelector(qual)?.classList.add('a-correr'));
  }

  /** As setas andam de separador em separador, como manda o teclado */
  #tecla(evento, i) {
    const passo = { ArrowRight: 1, ArrowLeft: -1, Home: -i, End: this.botoes.length - 1 - i }[
      evento.key
    ];
    if (passo === undefined) return;

    evento.preventDefault();
    const alvo = (i + passo + this.botoes.length) % this.botoes.length;

    // Sem isto o navegador arrasta a página para trazer o botão ao meio
    this.botoes[alvo].focus({ preventScroll: true });
    this.#carrega(alvo);
  }
}

if (!customElements.get('claro-info')) customElements.define('claro-info', ClaroInfo);
